import { Injectable } from '@angular/core';
import { StatusShape } from '../common/UI-Components/layer-status-design-picker/layer-status-design-picker.component';

@Injectable({
  providedIn: 'root'
})
export class StatusPlacemarkIconService {

  private readonly iconUrlCache = new Map<string, string>();
  constructor() { }

  private  isDark(hexColor) {
    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    const threshold = 0.8;
    return luminance < threshold;
  }

  public getStatusPlacemarkIconUrl(color: string, shape = StatusShape.Square): string {
    const cacheKey = `${color}_${shape}`;
    if (this.iconUrlCache.has(cacheKey)) {
      return this.iconUrlCache.get(cacheKey);
    }

    const isDark: boolean = this.isDark(color);
    const borderColor: string = isDark ? 'ffffff' : '000000';
    let img = '';
    switch (shape) {
      case StatusShape.Circle:
        img = this.getCircleIconSvg(color, borderColor);
        break;
      case StatusShape.Square:
        img = this.getSquareIconSvg(color, borderColor);
        break;
      case StatusShape.Triangle:
        img = this.getTriangleIconSvg(color, borderColor);
        break;
    }
    const blob = new Blob([img], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    this.iconUrlCache.set(cacheKey, url);
    return url;
  }

  private getCircleIconSvg(color: string, borderColor: string): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <defs>
      <radialGradient id="circleBorderGradient" cx="50%" cy="50%" r="52%">
        <stop offset="150%" stop-color="#bdc3c7" stop-opacity="0.2"/>
        <stop offset="100%" stop-color="#${color}" stop-opacity="0"/>
      </radialGradient>
      <filter id="blur">
      <feGaussianBlur in="SourceGraphic" stdDeviation="0.3"/>
      </filter>
      </defs>
      <g>
      <circle cx="12" cy="12" r="11" fill="#${color}" stroke="url(#circleBorderGradient)" stroke-width="1" filter="url(#blur)"/>
      <circle cx="12" cy="7" r="1.6" fill="#${color}" stroke="#${borderColor}" stroke-width="0.7" />
      <circle cx="12" cy="12" r="1.6" fill="#${color}" stroke="#${borderColor}" stroke-width="0.7" />
      <circle cx="12" cy="17" r="1.6" fill="#${color}" stroke="#${borderColor}" stroke-width="0.7" />
      </g>
    </svg>`;
  }

  private getSquareIconSvg(color: string, borderColor: string): string {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <defs>
      <radialGradient id="circleBorderGradient" cx="50%" cy="50%" r="70%">
        <stop offset="150%" stop-color="#4f585d" stop-opacity="0.2"/>
        <stop offset="100%" stop-color="#${color}" stop-opacity="0"/>
      </radialGradient>
      <filter id="blur">
      <feGaussianBlur in="SourceGraphic" stdDeviation="0.5"/>
      </filter>
      </defs>
      <g>
        <rect x="2" y="2" width="20" height="20" fill="#${color}" stroke="url(#circleBorderGradient)" stroke-width="0.5" filter="url(#blur)"/>
        <circle cx="12" cy="7" r="1.6" fill="#${color}" stroke="#${borderColor}" stroke-width="0.7" />
        <circle cx="12" cy="12" r="1.6" fill="#${color}" stroke="#${borderColor}" stroke-width="0.7" />
        <circle cx="12" cy="17" r="1.6" fill="#${color}" stroke="#${borderColor}" stroke-width="0.7" />
      </g>
    </svg>`
  }

  private getTriangleIconSvg(color: string, borderColor: string): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
  <defs>
    <filter id="blur-effect" x="-10%" y="-10%" width="120%" height="120%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="0.2"/>
    </filter>
    
    <radialGradient id="triangle-gradient" cx="50%" cy="40%" r="100%">
      <stop offset="0%" style="stop-color:#${color};stop-opacity:1"/>
      <stop offset="70%" style="stop-color:#${color};stop-opacity:1"/>
      <stop offset="100%" style="stop-color:#${color};stop-opacity:0.2"/>
    </radialGradient>

    <linearGradient id="border-gradient" x1="0%" y1="0%" x2="200%" y2="200%">
      <stop offset="0%" style="stop-color:#bdc3c7;stop-opacity:0.1"/>
      <stop offset="100%" style="stop-color:#bdc3c7;stop-opacity:0"/>
    </linearGradient>
  </defs>
  
  <g>
    <!-- Border triangle -->
    <polygon points="12,1 23,18.5 1,18.5" 
             fill="url(#border-gradient)"
             filter="url(#blur-effect)"
             stroke="#bdc3c7"
             stroke-width="1.5"
             stroke-opacity="0.2"/>

    <!-- Main triangle -->
    <polygon points="12,2 22,18 2,18" 
             fill="url(#triangle-gradient)" 
             filter="url(#blur-effect)"/>
    
    <circle cx="12" cy="8" r="1.2" fill="#${color}" stroke="#${borderColor}" stroke-width="0.7" />
    <circle cx="12" cy="11.5" r="1.2" fill="#${color}" stroke="#${borderColor}" stroke-width="0.7" />
    <circle cx="12" cy="15" r="1.2" fill="#${color}" stroke="#${borderColor}" stroke-width="0.7" />
  </g>
</svg>`;
  }
}
